import { Component, Input, OnInit, Inject } from "@angular/core";
import * as fileHelper from "../../../shared/file.helper";

import { CompetenceTypeDataService } from "../../../core/competencetypes.dataservice";
import { CompetenceGroupsService } from "../../../core/competencegroups.dataservice";
import { AuthServerProvider } from "../../../shared/auth/auth.service";
import { CrewPermissionsService } from "../../../core/crew-permissions.service";

@Component({
    selector: "crew-person-competences",
    template: require('./person.competences.component.html')
})
export class PersonCompetencesComponent implements OnInit {

    public isCheckedValid: boolean = true;
    public isCheckedWarning: boolean = true;
    public isCheckedExpired: boolean = false;
    public isCheckedFuture: boolean = true;

    isPermissionWriteOrAdmin: boolean;

    constructor(
        @Inject(CompetenceTypeDataService.Id) private competenceTypeDataService: CompetenceTypeDataService,
        @Inject(CompetenceGroupsService.Id) private competenceGroupService: CompetenceGroupsService,
        private authServerProvider: AuthServerProvider,
        private crewPermissionsService: CrewPermissionsService) { }

    competenceGroups: Nema.Crew.Model.CompetenceGroup[];
    private groupedPersonCompetences: Map<number, Nema.Crew.Model.PersonCompetence[]>;
    private competenceGroupAvailability: Map<number, boolean>;

    @Input()
    person: Nema.Crew.Model.Person;

    ngOnInit() {
        this.crewPermissionsService.isPermissionWriteOrAdmin().subscribe(value => this.isPermissionWriteOrAdmin = value);

        Promise.resolve()
            .then(() => (<any>this.person.PersonCompetences).load())
            .then(() => this.getCompetenceTypesAndCompetenceGroups())
            .then(res => {
                this.competenceGroups = res[1];
                return res;
            })
            .then(res => this.competenceGroupAvailability = PersonCompetencesComponent.createCompetenceGroupAvailability(res[0], res[1]))
            .then(() => this.createGroupedPersonCompetences())
            .then(res => this.groupedPersonCompetences = res);
    }

    private getCompetenceTypesAndCompetenceGroups(): Promise<[Nema.Crew.Model.CompetenceType[], Nema.Crew.Model.CompetenceGroup[]]> {
        return Promise.resolve()
            .then(() => this.competenceTypeDataService.getAll()
                .then(competenceTypes => this.competenceGroupService.getAll()
                    .then<[Nema.Crew.Model.CompetenceType[], Nema.Crew.Model.CompetenceGroup[]], never>(
                        competenceGroups => [competenceTypes, competenceGroups])));
    }

    private static createCompetenceGroupAvailability(
        competenceTypes: Nema.Crew.Model.CompetenceType[],
        competenceGroups: Nema.Crew.Model.CompetenceGroup[]): Map<number, boolean> {
        return competenceGroups.reduce(
            (acc, currentCompetenceGroup) => acc.set(
                currentCompetenceGroup.Id,
                competenceTypes
                    .filter(competenceType => competenceType.CompetenceGroupId === currentCompetenceGroup.Id)
                    .length > 0),
            new Map<number, boolean>());
    }

    private createGroupedPersonCompetences(): Promise<Map<number, Nema.Crew.Model.PersonCompetence[]>> {
        return PersonCompetencesComponent.reducePromise(
            this.person.PersonCompetences,
            (acc, currentPersonCompetence) => this.competenceTypeDataService
                .get(currentPersonCompetence.CompetenceTypeId)
                .then(competenceType => {
                    const competenceGroupId = competenceType.CompetenceGroupId;

                    if (!acc.has(competenceGroupId)) {
                        acc.set(competenceGroupId, []);
                    }

                    acc.get(competenceGroupId).push(currentPersonCompetence);
                    return acc;
                }),
            new Map<number, Nema.Crew.Model.PersonCompetence[]>());
    }

    private static reducePromise<TItem, TResult>(
        items: TItem[],
        callbackfn: (acc: TResult, item: TItem) => TResult | PromiseLike<TResult>,
        initialValue: TResult): Promise<TResult> {
        return items.reduce(
            (accPromise, currentItem) => accPromise.then(acc => callbackfn(acc, currentItem)),
            Promise.resolve(initialValue));
    }

    public downloadFile(personCompetence: Nema.Crew.Model.PersonCompetence) {
        window.open(fileHelper.getPersonCompetenceFileDownloadUrl(personCompetence.Id, this.authServerProvider.getToken()));
    }

    getColor(fromDate: Date, expirationDate: Date): string {
        return this.competenceDateCalculation(
            "lawngreen",
            "yellow",
            "red",
            "deepskyblue",
            fromDate,
            expirationDate);
    }


    private showCompetence(fromDate: Date, expirationDate: Date): boolean {
        return this.competenceDateCalculation(
            this.isCheckedValid,
            this.isCheckedWarning,
            this.isCheckedExpired,
            this.isCheckedFuture,
            fromDate,
            expirationDate);
    }

    private competenceDateCalculation<T>(
        valueWhenValid: T,
        valueWhenWarning: T,
        valueWhenExpired: T,
        valueWhenFuture: T,
        fromDate: Date,
        expirationDate: Date): T {

        const now = Date.now();

        if (fromDate && fromDate.valueOf() > now) {
            return valueWhenFuture;
        }

        if (!expirationDate) {
            return valueWhenValid;
        }

        const diff = expirationDate.valueOf() - now;
        const diffDays = Math.ceil(diff / (1000 * 3600 * 24));

        if (diffDays >= 180) {
            return valueWhenValid;
        }
        else if (diffDays >= 0) {
            return valueWhenWarning;
        }
        else {
            return valueWhenExpired;
        }
    }

    private filterFunction(competences: Nema.Crew.Model.PersonCompetence[]): Nema.Crew.Model.PersonCompetence[] {
        return competences.filter(i => this.showCompetence(i.ValidFrom, i.Expiration));
    }

    getItems(groupid: number): Nema.Crew.Model.PersonCompetence[] {
        if (!this.groupedPersonCompetences || !this.groupedPersonCompetences.has(groupid)) {
            return [];
        }

        return this.filterFunction(this.groupedPersonCompetences.get(groupid));
    }

    hasItems(groupid: number): boolean {
        return this.getItems(groupid).length > 0;
    }

    hasAvailableItems(groupid: number): boolean {
        return this.competenceGroupAvailability
            && this.competenceGroupAvailability.has(groupid)
            && this.competenceGroupAvailability.get(groupid);
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/resources/persons/competences/person.competences.component.ts